import { Box, Typography } from "@mui/material";
import { DataGrid, GridToolbarQuickFilter, GridToolbarFilterButton,
    GridToolbarContainer, 
    GridToolbarExport, 
    gridFilteredSortedRowIdsSelector, 
    GridToolbarDensitySelector, 
    GridToolbarColumnsButton,
    useGridApiRef} from '@mui/x-data-grid';
import PlaceIcon from '@mui/icons-material/Place'
import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const GridToolbarWithExportOptions = () => {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <GridToolbarExport
                csvOptions={{
                    getRowsToExport: (params) => gridFilteredSortedRowIdsSelector(params.apiRef),
                    fileName: `Export`,
                    delimiter: ',',
                    utf8WithBom: true,
                }}
                printOptions={{
                    hideFooter: true,
                    hideToolbar: true,
                }}
            />
        </GridToolbarContainer>
    );
}

const TopFullWidthQuickSearchToolbar = ({ lightMode = false }) => {
    return (
        <Box sx={{ 
            p: '0 10px',
            pb: 0,
            '& .MuiInputBase-root': {
                backgroundColor: lightMode ? '#f8f9fa' : 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                border: lightMode ? '1px solid #e0e0e0' : '1px solid rgba(255,255,255,0.1)',
                '&:hover': {
                    backgroundColor: lightMode ? '#f0f0f0' : 'rgba(255,255,255,0.08)',
                    borderColor: lightMode ? '#5364FF' : 'rgba(255,255,255,0.2)',
                },
                '&.Mui-focused': {
                    backgroundColor: lightMode ? '#ffffff' : 'rgba(255,255,255,0.1)',
                    borderColor: lightMode ? '#5364FF' : '#5364FF',
                    boxShadow: lightMode ? '0 0 0 2px rgb(83 100 255 / 20%)' : '0 0 0 2px rgba(83, 100, 255, 0.2)',
                }
            },
            '& .MuiInputBase-input': {
                color: lightMode ? '#333' : 'white',
                fontSize: '14px',
                padding: '8px 12px',
                '&::placeholder': {
                    color: lightMode ? '#666' : 'rgba(255,255,255,0.7)',
                    opacity: 1,
                }
            },
            '& .MuiSvgIcon-root': {
                color: lightMode ? '#666' : 'rgba(255,255,255,0.7)',
                fontSize: '20px',
                marginLeft: '8px',
            }
        }} >
            <GridToolbarQuickFilter 
                sx={{ width: '100%' }}
                placeholder={lightMode ? "검색어를 입력하세요..." : "Search..."}
            />
        </Box>
    );
}

export const Link2SelectViewDataGrid = (props) => {
    const { lightMode = false, rowSelectionModel } = props;
    const apiRef = useGridApiRef();
    const [collapsedGroups, setCollapsedGroups] = React.useState([]);
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 100,
    });
    const lastProcessedId = React.useRef(null);
    React.useEffect(() => {
        if (rowSelectionModel && rowSelectionModel.length > 0) {
            const currentId = rowSelectionModel[0];
            if (lastProcessedId.current !== currentId) {
                lastProcessedId.current = null; 
            }
        } else {
            lastProcessedId.current = null;
        }
    }, [rowSelectionModel]);
    const toggleGroup = (groupName) => {
        setCollapsedGroups(prev => 
            prev.includes(groupName) 
                ? prev.filter(g => g !== groupName) 
                : [...prev, groupName]
        );
    };

    const groupedRows = React.useMemo(() => {
        if (!props.rows || props.rows.length === 0) return [];

        const processed = [];
        const groups = {};
        props.rows.forEach(row => {
            const match = row.DSP_NAME ? row.DSP_NAME.match(/^(.+?)\s*\(/) : null;
            const groupName = match ? match[1] : "기타";
            if (!groups[groupName]) groups[groupName] = [];
            groups[groupName].push(row);
        });

        Object.keys(groups).forEach((groupName, index) => {
            const isCollapsed = collapsedGroups.includes(groupName);
            processed.push({
                id: `GROUP_HEADER_${index}`,
                DSP_NAME: groupName,
                isGroupHeader: true,
                isCollapsed: isCollapsed
            });
            if (!isCollapsed) {
                groups[groupName].forEach(child => {
                    processed.push({
                        ...child,
                        isGroupHeader: false,
                    });
                });
            }
        });

        return processed;
    }, [props.rows, collapsedGroups]);

    React.useEffect(() => {
        if (rowSelectionModel && rowSelectionModel.length > 0 && props.rows && apiRef.current) {
            const targetId = rowSelectionModel[0];
            if (lastProcessedId.current === targetId) return;
            const targetRow = props.rows.find(row => {
                const rowId = row.LINK2_ID ? String(row.LINK2_ID) : String(row.id);
                return rowId === targetId;
            });
            if (targetRow) {
                const match = targetRow.DSP_NAME ? targetRow.DSP_NAME.match(/^(.+?)\s*\(/) : null;
                const groupName = match ? match[1] : "기타";
                if (collapsedGroups.includes(groupName)) {
                    setCollapsedGroups(prev => prev.filter(g => g !== groupName));
                    return; 
                }
            }
            const globalIndex = groupedRows.findIndex(row => {
                const rowId = row.isGroupHeader 
                    ? String(row.id) 
                    : (row.LINK2_ID ? String(row.LINK2_ID) : String(row.id));
                return rowId === targetId;
            });

            if (globalIndex >= 0) {
                lastProcessedId.current = targetId;
                const targetPage = Math.floor(globalIndex / paginationModel.pageSize);
                if (paginationModel.page !== targetPage) {
                    setPaginationModel(prev => ({ ...prev, page: targetPage }));
                }
                setTimeout(() => {
                    if (apiRef.current) {
                        apiRef.current.scrollToIndexes({ rowIndex: globalIndex });
                    }
                }, 150);
            }
        }
    }, [rowSelectionModel, groupedRows, apiRef, props.rows, collapsedGroups, paginationModel.pageSize]);

    const columns = [
        {
            field: "DSP_NAME",
            headerName: "도로구간명 (시점-종점)",
            flex: 1,
            minWidth: 300,
            renderCell: (params) => {
                const isHeader = params.row.isGroupHeader;
                const isCollapsed = params.row.isCollapsed;

                return (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        pl: isHeader ? 0 : 2,
                        width: '100%',
                        height: '100%',
                        cursor: isHeader ? 'pointer' : 'default'
                    }}
                    onClick={(e) => {
                        if (isHeader) {
                            e.stopPropagation();
                            toggleGroup(params.value);
                        }
                    }}
                    >
                        {isHeader ? (
                            <Typography sx={{ fontWeight: 'bold', fontSize:'14px', color: lightMode ? '#333' : '#fff', display: 'flex', alignItems: 'center' }}>
                                <ExpandMoreIcon sx={{ 
                                    mr: 1, 
                                    fontSize: '14px', 
                                    color: lightMode ? '#333' : '#fff',
                                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', 
                                    transition: 'transform 0.2s ease-in-out'
                                }} />
                                {params.value}
                            </Typography>
                        ) : (
                            <Typography sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: 'white' }}>
                                <PlaceIcon sx={{ mr: 1, fontSize: '1rem', mb: -0.2, color: '#5364FF' }} />
                                {params.value}
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
    ];

    return (
        <DataGrid
            {...props}
            apiRef={apiRef}
            rows={groupedRows}
            columns={columns}
            rowSelectionModel={rowSelectionModel}
            slots={{ toolbar: () => <TopFullWidthQuickSearchToolbar lightMode={lightMode} /> }}
            getRowClassName={(params) => params.row.isGroupHeader ? 'group-header-row' : ''}
            onRowClick={(params, event) => {
                if (params.row.isGroupHeader) return;
                if (props.onRowClick) props.onRowClick(params, event);
            }}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
            sx={{
                  // 기본 row
                '& .MuiDataGrid-row': {
                    transition: 'background-color 0.15s ease',
                },
                // Hover
                '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(83, 100, 255, 0.12)',
                },
                // Active (마우스 누르고 있을 때)
                '& .MuiDataGrid-row:active': {
                    backgroundColor: 'rgba(83, 100, 255, 0.25)',
                },
                // Selected Row
                '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: 'rgb(83 100 255 / 50%) !important',
                },
                // Selected + Hover
                '& .MuiDataGrid-row.Mui-selected:hover': {
                    backgroundColor: 'rgb(83 100 255 / 50%) !important',
                },
                minWidth: '100px',
                border: 'none',
                '& .group-header-row': {
                    bgcolor: 'rgba(83, 100, 255, 0.2)',
                    color:'#fff',
                    '&:hover': { bgcolor: 'rgba(83, 100, 255,0.2)' },
                },
                '& .MuiDataGrid-footerContainer': {
                    '& *' : {  color: '#333' }
                },
                '& .MuiTablePagination-root': {
                    height: '36px',
                    minHeight: '36px',
                    overflow: 'hidden',
                },
                '& .MuiTablePagination-toolbar': {
                    minHeight: '36px',
                    height: '36px',
                    paddingY: '0',
                    paddingRight: '2px',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    margin: 0,
                    fontSize: '14px',
                },
                '& .MuiTablePagination-actions': {
                    '& button': {
                        padding: '2px',
                    }
                },
                '& .MuiInputBase-root': {
                    marginRight: '8px',
                    fontSize: '14px',
                },
                '& .MuiDataGrid-main': { color: 'white', overflowX: 'hidden' },
                '& .MuiDataGrid-columnHeaders': { bgcolor: 'rgba(68, 78, 94, 0.5)', color: 'white' },
                '& .MuiDataGrid-cell': { borderBottom: '1px solid rgba(255,255,255,0.05)', '&:focus': { outline: 'none' } },
                '& ::-webkit-scrollbar': { width: '6px', height: '6px' },
                '& ::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
                ...props.sx
            }}
            density="compact"
            hideFooterSelectedRowCount
        />
    );
}

export const Link2SelectCrsrdDataGrid = (props) => {
    const { rows, loading, onRowClick, lightMode = false, rowSelectionModel, ...otherProps } = props;
    const apiRef = useGridApiRef();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 100,
    });
    React.useEffect(() => {
        if (rowSelectionModel && rowSelectionModel.length > 0 && rows && apiRef.current) {
            const targetId = rowSelectionModel[0];
            const globalIndex = rows.findIndex(row => {
                const rowId = row.crsrdId ? String(row.crsrdId) : String(row.id);
                return rowId === targetId;
            });

            if (globalIndex >= 0) {
                const targetPage = Math.floor(globalIndex / paginationModel.pageSize);
                if (paginationModel.page !== targetPage) {
                    setPaginationModel(prev => ({ ...prev, page: targetPage }));
                }
                setTimeout(() => {
                    apiRef.current.scrollToIndexes({ rowIndex: globalIndex });
                }, 100);
            }
        }
    }, [rowSelectionModel, rows, apiRef, paginationModel.pageSize]);
    const TopFullWidthQuickSearchToolbar = () => {
        return (
            <Box sx={{ 
                p: '0 10px',
                pb: 0,
                '& .MuiInputBase-root': {
                    backgroundColor: lightMode ? '#f8f9fa' : 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    border: lightMode ? '1px solid #e0e0e0' : '1px solid rgba(255,255,255,0.1)',
                    '&:hover': {
                        backgroundColor: lightMode ? '#f0f0f0' : 'rgba(255,255,255,0.08)',
                        borderColor: lightMode ? '#5364FF' : 'rgba(255,255,255,0.2)',
                    },
                    '&.Mui-focused': {
                        backgroundColor: lightMode ? '#ffffff' : 'rgba(255,255,255,0.1)',
                        borderColor: lightMode ? '#5364FF' : '#5364FF',
                        boxShadow: lightMode ? '0 0 0 2px rgb(83 100 255 / 20%)' : '0 0 0 2px rgba(83, 100, 255, 0.2)',
                    }
                },
                '& .MuiInputBase-input': {
                    color: lightMode ? '#333' : 'white',
                    fontSize: '14px',
                    padding: '8px 12px',
                    '&::placeholder': {
                        color: lightMode ? '#666' : 'rgba(255,255,255,0.7)',
                        opacity: 1,
                    }
                },
                '& .MuiSvgIcon-root': {
                    color: lightMode ? '#666' : 'rgba(255,255,255,0.7)',
                    fontSize: '20px',
                    marginLeft: '8px',
                }
            }}>
                <GridToolbarQuickFilter 
                    sx={{ width: '100%' }}
                    placeholder={lightMode ? "교차로명을 검색하세요..." : "Search..."}
                />
            </Box>
        );
    }
    const columns = [
        {
            field: "crsrdNm",
            headerName: "교차로 명칭",
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {/* <AddIcon sx={{ mr: 1, fontSize: '1.2rem', color: '#5364FF' }} /> */}
                    <Box
                        component="img"
                        src="/images/crsrd_icon.png"
                        alt="icon"
                        sx={{ 
                            width: 18,
                            height: 18,
                            mr: 1.5,
                            objectFit: 'contain' 
                        }}
                    />
                    <Typography sx={{ fontSize: '14px', color: lightMode ? '#333' : 'white' }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        }
    ];

    return (
        <DataGrid
            apiRef={apiRef}
            rows={rows || []}
            columns={columns}
            loading={loading}
            onRowClick={onRowClick}
            rowSelectionModel={rowSelectionModel}
            slots={{ toolbar: TopFullWidthQuickSearchToolbar }}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
            density="compact"
            disableColumnMenu
            hideFooterSelectedRowCount
            {...otherProps}
            sx={{
                height: '100%',
                width: '100%',
                border: 'none',
                // 기본 row
                '& .MuiDataGrid-row': {
                    transition: 'background-color 0.15s ease',
                },

                // Hover
                '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(83, 100, 255, 0.12)',
                },

                // Active (마우스 누르고 있을 때)
                '& .MuiDataGrid-row:active': {
                    backgroundColor: 'rgba(83, 100, 255, 0.25)',
                },

                // Selected Row
                '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: 'rgb(83 100 255 / 50%) !important',
                },

                // Selected + Hover
                '& .MuiDataGrid-row.Mui-selected:hover': {
                    backgroundColor: 'rgb(83 100 255 / 50%) !important',
                },
                '& .MuiDataGrid-main': { 
                    color: 'white', 
                    overflowX: 'hidden',
                    bgcolor: 'transparent'
                },
                '& .MuiDataGrid-columnHeaders': {
                    color: lightMode ? '#333' : 'white',
                    bgcolor: lightMode ? '#f5f5f5' : 'transparent',
                    borderBottom: lightMode ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                    fontSize:'14px',
                },
                '& .MuiDataGrid-cell': { 
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    '&:focus': { outline: 'none' }
                },
                '& .MuiDataGrid-row:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                },
                '& .MuiDataGrid-footerContainer': {
                    '& *' : {  color: '#333' }
                },
                
                '& .MuiTablePagination-root': {
                    height: '36px',
                    minHeight: '36px',
                    overflow: 'hidden',
                },
                '& .MuiTablePagination-toolbar': {
                    minHeight: '36px',
                    height: '36px',
                    paddingY: '0',
                    paddingRight: '2px',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    margin: 0,
                    fontSize: '14px',
                },
                '& .MuiTablePagination-actions': {
                    '& button': {
                        padding: '2px',
                    }
                },
                '& .MuiInputBase-root': {
                    marginRight: '8px',
                    fontSize: '14px',
                },
                '& ::-webkit-scrollbar': { width: '6px', height: '6px' },
                '& ::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: '3px' }
            }}
        />
    );
}

export const Link2StatisticsViewDataGrid = (props) => {
    return (<DataGrid
        density="standard"
        slots={{
            toolbar: GridToolbarWithExportOptions,
        }}
        hideFooterSelectedRowCount
        {...props}
        sx={{
        // 기본 row
        '& .MuiDataGrid-row': {
            transition: 'background-color 0.15s ease',
        },

        // Hover
        '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(83, 100, 255, 0.12)',
        },

        // Active (마우스 누르고 있을 때)
        '& .MuiDataGrid-row:active': {
            backgroundColor: 'rgba(83, 100, 255, 0.2)',
        },

        // Selected Row
        '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'rgb(83 100 255 / 30%) !important',
        },

        // Selected + Hover
        '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: 'rgb(83 100 255 / 40%) !important',
        },
        // 툴바 전체 영역
        '& .MuiDataGrid-toolbarContainer': {
          backgroundColor: 'rgba(30,30,30,0.8)',
          padding: '4px 8px',
        },
        // 툴바 버튼 공통 스타일
        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
            color: '#919cff',
            borderColor: '#5364FF',
            fontSize: '12px',
            minHeight: '28px',
            padding: '2px 10px',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            '&:hover': {
            backgroundColor: 'rgba(83,100,255,0.15)',
            borderColor: '#6C7AFC',
            color: '#6C7AFC',
            },
        },
        // 아이콘 색상
        '& .MuiDataGrid-toolbarContainer .MuiSvgIcon-root': {
            color: '#919cff',
            fontSize: '18px',
        },
        // Quick Filter 인풋
        '& .MuiDataGrid-toolbarContainer .MuiInputBase-root': {
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: '6px',
            padding: '2px 6px',
        },
        ...props.sx,
        }} 
        Pagination={false}
    />)
}
